<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import { UiAside, UiButton, UiDialog, UiIcon, UiTabs, UiTabsItem, UiText, useToaster } from "@shopexperts/shared";
import type { BanRule, BanRuleTestResult, BannedEmail, BannedIp, TestBanRuleRequest } from "@shopexperts/legacy-api";
import BanListPrimer from "@/app/components/ban-list/BanListPrimer.vue";
import BanRuleTester from "@/app/components/ban-list/BanRuleTester.vue";
import BanRulesPanel from "@/app/components/ban-list/BanRulesPanel.vue";
import BanRuleForm from "@/app/components/ban-list/BanRuleForm.vue";
import BannedEmailsPanel from "@/app/components/ban-list/BannedEmailsPanel.vue";
import BannedEmailForm from "@/app/components/ban-list/BannedEmailForm.vue";
import BannedIpsPanel from "@/app/components/ban-list/BannedIpsPanel.vue";
import BannedIpForm from "@/app/components/ban-list/BannedIpForm.vue";
import { useBanListPage } from "@/app/composables/ban-list/useBanListPage";
import {
    buildDeleteRuleCopy,
    buildRemoveIpCopy,
    buildUnbanEmailCopy,
    defaultBanRuleFormValues,
    defaultBannedEmailFormValues,
    defaultBannedIpFormValues,
    formValuesToBanEmailPayload,
    formValuesToCreateRulePayload,
    formValuesToRecordIpPayload,
    formValuesToUpdateRulePayload,
    ruleToFormValues,
    type BanRuleFormValues,
    type BannedEmailFormValues,
    type BannedIpFormValues,
} from "@/app/composables/ban-list/banListForms";
import { buildIpRegistryExport } from "@/app/composables/ban-list/banListFormatters";
import { extractStatus, mapValidationErrors } from "@/app/composables/claim/claimApiErrors";

type PanelMode = "rule-create" | "rule-edit" | "email-create" | "ip-create";

const {
    activeTab,

    ruleSearchInput,
    ruleTypeFilter,
    ruleActiveFilter,
    rules,
    rulesResult,
    loadedRulesSummary,
    hasRuleFilters,
    resetRuleFilters,

    emailSearchInput,
    emailSourceFilter,
    emailRuleFilter,
    bannedEmails,
    emailsResult,
    hasEmailFilters,
    resetEmailFilters,

    ipSearchInput,
    ipSourceFilter,
    ipRuleFilter,
    bannedIps,
    ipsResult,
    hasIpFilters,
    resetIpFilters,

    ruleOptions,
    ruleTypeOptions,
    showEmailsForRule,
    showIpsForRule,

    createRule,
    updateRule,
    deleteRule,
    testRules,
    banEmail,
    unbanEmail,
    recordIp,
    removeIp,
} = useBanListPage();

const toaster = useToaster();

const panelOpen = ref(false);
const panelMode = ref<PanelMode>("rule-create");
const editingRule = ref<BanRule | null>(null);
const serverErrors = ref<Record<string, string | undefined> | undefined>(undefined);

const ruleFormValues = ref<BanRuleFormValues>(defaultBanRuleFormValues());
const emailFormValues = ref<BannedEmailFormValues>(defaultBannedEmailFormValues());
const ipFormValues = ref<BannedIpFormValues>(defaultBannedIpFormValues());

const emailFormEl = useTemplateRef<{ resetForm: () => void }>("emailFormEl");
const ipFormEl = useTemplateRef<{ resetForm: () => void }>("ipFormEl");

const testerRef = useTemplateRef<HTMLElement>("testerRef");
const testResult = ref<BanRuleTestResult | null>(null);

const deleteRuleOpen = ref(false);
const rulePendingDelete = ref<BanRule | null>(null);

const unbanOpen = ref(false);
const banPendingRemoval = ref<BannedEmail | null>(null);

const removeIpOpen = ref(false);
const ipPendingRemoval = ref<BannedIp | null>(null);

const isSubmitting = computed(() => (
    createRule.isPending.value
    || updateRule.isPending.value
    || banEmail.isPending.value
    || recordIp.isPending.value
));

const isMutating = computed(() => (
    isSubmitting.value
    || deleteRule.isPending.value
    || unbanEmail.isPending.value
    || removeIp.isPending.value
));

/**
 * UiAside hides the panel with `v-show`, so the rule form stays mounted between
 * opens. Keying on an open counter remounts it every time, which is what clears
 * vee-validate's touched/error state *and* the terms field's uncommitted draft —
 * `resetForm()` reaches neither.
 */
const rulePanelOpens = ref(0);
const ruleFormKey = computed(() => `rule-${editingRule.value?.id ?? "create"}-${rulePanelOpens.value}`);

const deleteRuleCopy = computed(() => buildDeleteRuleCopy(rulePendingDelete.value));
const unbanCopy = computed(() => buildUnbanEmailCopy(banPendingRemoval.value));
const removeIpCopy = computed(() => buildRemoveIpCopy(ipPendingRemoval.value));

const panelTitle = computed(() => {
    switch (panelMode.value) {
        case "rule-edit":
            return "Edit ban rule";
        case "email-create":
            return "Ban an email";
        case "ip-create":
            return "Add an IP address";
        default:
            return "Create ban rule";
    }
});

const panelDescription = computed(() => {
    switch (panelMode.value) {
        case "rule-edit":
            return "Change the terms, the matcher or the status. Existing bans keep pointing at this rule.";
        case "email-create":
            return "Blocks the address on every screened endpoint and locks the account behind it, if there is one.";
        case "ip-create":
            return "Adds an address to the export registry. Nothing is blocked by the API itself.";
        default:
            return "One bannable offence: a list of terms that must all appear in the same field.";
    }
});

watch(panelOpen, (open) => {
    if (!open) {
        editingRule.value = null;
        serverErrors.value = undefined;
    }
});

function clearServerErrors() {
    if (serverErrors.value) serverErrors.value = undefined;
}

function openRuleCreate() {
    panelMode.value = "rule-create";
    editingRule.value = null;
    serverErrors.value = undefined;
    ruleFormValues.value = defaultBanRuleFormValues();
    rulePanelOpens.value += 1;
    panelOpen.value = true;
}

function openRuleEdit(rule: BanRule) {
    panelMode.value = "rule-edit";
    editingRule.value = rule;
    serverErrors.value = undefined;
    ruleFormValues.value = ruleToFormValues(rule);
    rulePanelOpens.value += 1;
    panelOpen.value = true;
}

function openEmailCreate() {
    panelMode.value = "email-create";
    serverErrors.value = undefined;
    emailFormValues.value = defaultBannedEmailFormValues();
    panelOpen.value = true;
    void nextTick(() => emailFormEl.value?.resetForm());
}

function openIpCreate() {
    panelMode.value = "ip-create";
    serverErrors.value = undefined;
    ipFormValues.value = defaultBannedIpFormValues();
    panelOpen.value = true;
    void nextTick(() => ipFormEl.value?.resetForm());
}

function closePanel() {
    panelOpen.value = false;
}

/**
 * Laravel answers a bad payload with 422; anything else is toasted by the
 * mutation. The panel stays open either way so a 500, a 429 or a dropped
 * connection does not discard everything the admin just typed.
 */
function handleMutationError(error: unknown) {
    if (extractStatus(error) === 422) {
        serverErrors.value = mapValidationErrors(error);
    }
}

async function submitRule(values: BanRuleFormValues) {
    serverErrors.value = undefined;
    try {
        if (panelMode.value === "rule-edit" && editingRule.value) {
            await updateRule.mutateAsync({
                ruleId: editingRule.value.id,
                body: formValuesToUpdateRulePayload(values),
            });
        } else {
            await createRule.mutateAsync(formValuesToCreateRulePayload(values));
        }
        closePanel();
    } catch (error) {
        handleMutationError(error);
    }
}

async function submitBannedEmail(values: BannedEmailFormValues) {
    serverErrors.value = undefined;
    try {
        await banEmail.mutateAsync(formValuesToBanEmailPayload(values));
        closePanel();
    } catch (error) {
        handleMutationError(error);
    }
}

async function submitBannedIp(values: BannedIpFormValues) {
    serverErrors.value = undefined;
    try {
        await recordIp.mutateAsync(formValuesToRecordIpPayload(values));
        closePanel();
    } catch (error) {
        handleMutationError(error);
    }
}

/** Deactivating is the reversible alternative to deleting, so it needs no dialog. */
async function toggleRuleActive(rule: BanRule) {
    try {
        await updateRule.mutateAsync({ ruleId: rule.id, body: { is_active: !rule.is_active } });
    } catch {
        // Toasted by the mutation.
    }
}

function openDeleteRule(rule: BanRule) {
    rulePendingDelete.value = rule;
    deleteRuleOpen.value = true;
}

async function confirmDeleteRule() {
    const target = rulePendingDelete.value;
    if (!target) return;
    try {
        await deleteRule.mutateAsync(target.id);
        deleteRuleOpen.value = false;
        rulePendingDelete.value = null;
    } catch {
        // Toasted by the mutation.
    }
}

function openUnban(ban: BannedEmail) {
    banPendingRemoval.value = ban;
    unbanOpen.value = true;
}

async function confirmUnban() {
    const target = banPendingRemoval.value;
    if (!target) return;
    try {
        await unbanEmail.mutateAsync(target.id);
        unbanOpen.value = false;
        banPendingRemoval.value = null;
    } catch {
        // Toasted by the mutation.
    }
}

function openRemoveIp(entry: BannedIp) {
    ipPendingRemoval.value = entry;
    removeIpOpen.value = true;
}

async function confirmRemoveIp() {
    const target = ipPendingRemoval.value;
    if (!target) return;
    try {
        await removeIp.mutateAsync(target.id);
        removeIpOpen.value = false;
        ipPendingRemoval.value = null;
    } catch {
        // Toasted by the mutation.
    }
}

async function runTest(payload: TestBanRuleRequest) {
    try {
        testResult.value = await testRules.mutateAsync(payload);
    } catch {
        testResult.value = null;
    }
}

/** "Test" on a rule card scrolls to the tester rather than opening a new surface. */
function scrollToTester() {
    activeTab.value = "rules";
    void nextTick(() => {
        testerRef.value?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
}

async function copyToClipboard(value: string, successTitle: string) {
    try {
        await navigator.clipboard.writeText(value);
        toaster.create({ title: successTitle, type: "success" });
    } catch {
        toaster.create({ title: "Could not copy to the clipboard", type: "error" });
    }
}

function copyIp(entry: BannedIp) {
    void copyToClipboard(entry.ip_address, "IP address copied");
}

function copyLoadedIps() {
    const value = buildIpRegistryExport(bannedIps.value);
    if (!value) return;
    void copyToClipboard(
        value,
        `Copied ${bannedIps.value.length} ${bannedIps.value.length === 1 ? "address" : "addresses"}`,
    );
}
</script>

<template>
    <main class="flex-1 overflow-y-auto bg-secondary p-8 font-light">
        <div class="mb-6 flex flex-col gap-2">
            <h1 class="text-3xl font-normal">Ban List</h1>
            <UiText
                as="p"
                variant="p"
                class="max-w-3xl text-se-gray-600"
                text="Spam screening for client project submissions: the rules that block them, the emails they banned, and the IP registry for the edge."
            />
        </div>

        <div class="mb-6">
            <BanListPrimer />
        </div>

        <UiTabs v-model="activeTab" default-value="rules">
            <UiTabsItem value="rules" label="Rules">
                <div class="flex flex-col gap-6 pt-6">
                    <div ref="testerRef">
                        <BanRuleTester
                            :is-testing="testRules.isPending.value"
                            :result="testResult"
                            @test="runTest"
                            @clear="testResult = null"
                        />
                    </div>

                    <BanRulesPanel
                        v-model:search-input="ruleSearchInput"
                        v-model:type-filter="ruleTypeFilter"
                        v-model:active-filter="ruleActiveFilter"
                        :rules="rules"
                        :type-options="ruleTypeOptions"
                        :summary="loadedRulesSummary"
                        :is-loading="rulesResult.isLoading.value"
                        :is-fetching="rulesResult.isFetching.value"
                        :has-next-page="rulesResult.hasNextPage.value"
                        :is-fetching-next-page="rulesResult.isFetchingNextPage.value"
                        :has-filters="hasRuleFilters"
                        :is-busy="isMutating"
                        @create="openRuleCreate"
                        @edit="openRuleEdit"
                        @toggle-active="toggleRuleActive"
                        @delete="openDeleteRule"
                        @show-emails="(rule: BanRule) => showEmailsForRule(rule.id)"
                        @show-ips="(rule: BanRule) => showIpsForRule(rule.id)"
                        @test="scrollToTester"
                        @load-more="rulesResult.fetchNextPage()"
                        @reset-filters="resetRuleFilters"
                    />
                </div>
            </UiTabsItem>

            <UiTabsItem value="emails" label="Banned Emails">
                <div class="pt-6">
                    <BannedEmailsPanel
                        v-model:search-input="emailSearchInput"
                        v-model:source-filter="emailSourceFilter"
                        v-model:rule-filter="emailRuleFilter"
                        :bans="bannedEmails"
                        :rule-options="ruleOptions"
                        :is-loading="emailsResult.isLoading.value"
                        :is-fetching="emailsResult.isFetching.value"
                        :has-next-page="emailsResult.hasNextPage.value"
                        :is-fetching-next-page="emailsResult.isFetchingNextPage.value"
                        :has-filters="hasEmailFilters"
                        :is-busy="isMutating"
                        @create="openEmailCreate"
                        @unban="openUnban"
                        @filter-by-rule="(ruleId: number) => (emailRuleFilter = String(ruleId))"
                        @load-more="emailsResult.fetchNextPage()"
                        @reset-filters="resetEmailFilters"
                    />
                </div>
            </UiTabsItem>

            <UiTabsItem value="ips" label="IP Registry">
                <div class="pt-6">
                    <BannedIpsPanel
                        v-model:search-input="ipSearchInput"
                        v-model:source-filter="ipSourceFilter"
                        v-model:rule-filter="ipRuleFilter"
                        :entries="bannedIps"
                        :rule-options="ruleOptions"
                        :is-loading="ipsResult.isLoading.value"
                        :is-fetching="ipsResult.isFetching.value"
                        :has-next-page="ipsResult.hasNextPage.value"
                        :is-fetching-next-page="ipsResult.isFetchingNextPage.value"
                        :has-filters="hasIpFilters"
                        :is-busy="isMutating"
                        @create="openIpCreate"
                        @remove="openRemoveIp"
                        @copy="copyIp"
                        @copy-all="copyLoadedIps"
                        @filter-by-rule="(ruleId: number) => (ipRuleFilter = String(ruleId))"
                        @load-more="ipsResult.fetchNextPage()"
                        @reset-filters="resetIpFilters"
                    />
                </div>
            </UiTabsItem>
        </UiTabs>

        <UiAside v-model="panelOpen" position="right" :close-on-overlay-click="false">
            <div class="flex max-h-full flex-col gap-4 overflow-y-auto px-4 py-6 md:p-8">
                <div class="flex items-start justify-between gap-4">
                    <div class="flex flex-col gap-1">
                        <UiText as="h3" variant="h3" class="font-semibold" :text="panelTitle" />
                        <UiText as="p" variant="p-small" class="text-se-gray-600" :text="panelDescription" />
                    </div>
                    <UiButton variant="ghost" shape="square" :disabled="isSubmitting" @click="closePanel">
                        <template #before_label>
                            <UiIcon name="close" class="h-6 w-6" />
                        </template>
                    </UiButton>
                </div>

                <BanRuleForm
                    v-if="panelMode === 'rule-create' || panelMode === 'rule-edit'"
                    :key="ruleFormKey"
                    v-model="ruleFormValues"
                    :is-editing="panelMode === 'rule-edit'"
                    :is-submitting="isSubmitting"
                    :type-options="ruleTypeOptions"
                    :server-errors="serverErrors"
                    @submit="submitRule"
                    @cancel="closePanel"
                    @change="clearServerErrors"
                />

                <BannedEmailForm
                    v-else-if="panelMode === 'email-create'"
                    ref="emailFormEl"
                    v-model="emailFormValues"
                    :is-submitting="isSubmitting"
                    :rule-options="ruleOptions"
                    :server-errors="serverErrors"
                    @submit="submitBannedEmail"
                    @cancel="closePanel"
                    @change="clearServerErrors"
                />

                <BannedIpForm
                    v-else
                    ref="ipFormEl"
                    v-model="ipFormValues"
                    :is-submitting="isSubmitting"
                    :rule-options="ruleOptions"
                    :server-errors="serverErrors"
                    @submit="submitBannedIp"
                    @cancel="closePanel"
                    @change="clearServerErrors"
                />
            </div>
        </UiAside>

        <UiDialog
            v-model="deleteRuleOpen"
            :loading="deleteRule.isPending.value"
            :title="deleteRuleCopy.title"
            :text="deleteRuleCopy.text"
            :primary-action-label="deleteRuleCopy.primaryActionLabel"
            :secondary-action-label="deleteRuleCopy.secondaryActionLabel"
            @primary-action="confirmDeleteRule"
            @secondary-action="deleteRuleOpen = false"
        />

        <UiDialog
            v-model="unbanOpen"
            :loading="unbanEmail.isPending.value"
            :title="unbanCopy.title"
            :text="unbanCopy.text"
            :primary-action-label="unbanCopy.primaryActionLabel"
            :secondary-action-label="unbanCopy.secondaryActionLabel"
            @primary-action="confirmUnban"
            @secondary-action="unbanOpen = false"
        />

        <UiDialog
            v-model="removeIpOpen"
            :loading="removeIp.isPending.value"
            :title="removeIpCopy.title"
            :text="removeIpCopy.text"
            :primary-action-label="removeIpCopy.primaryActionLabel"
            :secondary-action-label="removeIpCopy.secondaryActionLabel"
            @primary-action="confirmRemoveIp"
            @secondary-action="removeIpOpen = false"
        />
    </main>
</template>
